// Self-service kiosk contracts (module 05: PB-05-09..12, OR-04; PD-040, PD-041).
import { z } from 'zod';
import type { KioskBadgeStatus } from './portal.ts'; // PB-05-12 (PD-514)

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const KIOSK_WARNINGS = ['early', 'identity_evidence_missing', 'attendance_declined', 'preregistration_incomplete'] as const;

export const KioskSettingsSchema = z.object({
  defaultLocale: z.enum(['en', 'ar']).optional(),
  idleSeconds: z.number().int().min(30, 'v.invalid').max(600, 'v.invalid').optional(),
  warningSeconds: z.number().int().min(5, 'v.invalid').max(60, 'v.invalid').optional(),
  lookupCheck: z.enum(['name', 'phone_last4']).optional(),
  checkIn: z.boolean().optional(),
  selfCheckout: z.boolean().optional(),
  walkInRequests: z.boolean().optional(),
  walkInHostIds: z.array(Uuid).max(30).optional(),
  acceptNotices: z.boolean().optional(),
  escalateWarnings: z.array(z.enum(KIOSK_WARNINGS)).max(KIOSK_WARNINGS.length).optional(),
  // PB-05-12 (PD-514): "print" sends the badge to this kiosk's configured edge printer.
  badge: z.enum(['collect_at_reception', 'none', 'print']).optional(),
  badgePrinterId: Uuid.nullable().optional(),
});
export type KioskSettingsInput = z.infer<typeof KioskSettingsSchema>;

/** Settings with every default applied, as stored behaviour. */
export interface KioskSettingsView {
  readonly defaultLocale: 'en' | 'ar';
  readonly idleSeconds: number;
  readonly warningSeconds: number;
  readonly lookupCheck: 'name' | 'phone_last4';
  readonly checkIn: boolean;
  readonly selfCheckout: boolean;
  readonly walkInRequests: boolean;
  readonly walkInHostIds: readonly string[];
  readonly acceptNotices: boolean;
  readonly escalateWarnings: readonly (typeof KIOSK_WARNINGS)[number][];
  readonly badge: 'collect_at_reception' | 'none' | 'print';
  /** PB-05-12 (PD-514): the edge printer peripheral used when badge is "print". */
  readonly badgePrinterId: string | null;
}

// ------------------------------------------------------------------ administration (kiosk.manage)
export const KioskListQuery = z.object({ siteId: Uuid });
export const CreateKioskRequest = z.object({ siteId: Uuid, name: trimmed(80), settings: KioskSettingsSchema.default({}) });
export const UpdateKioskRequest = z.object({ expectedVersion: z.number().int().min(1), name: trimmed(80).optional(), settings: KioskSettingsSchema.optional() });
export const RevokeKioskRequest = z.object({ reason: trimmed(500) });

export interface KioskDeviceResponse {
  readonly id: string;
  readonly siteId: string;
  readonly name: string;
  readonly status: 'pending' | 'active' | 'revoked';
  readonly settings: KioskSettingsView;
  readonly pairingExpiresAt: string | null;
  readonly pairedAt: string | null;
  readonly lastSeenAt: string | null;
  readonly version: number;
}
/** The pairing code is shown once and never stored in clear. */
export interface KioskPairingResponse {
  readonly device: KioskDeviceResponse;
  readonly pairingCode: string;
  readonly pairingExpiresAt: string;
}

// ------------------------------------------------------------------ device (kiosk cookie)
export const KioskPairRequest = z.object({ code: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{4}-?[A-Z0-9]{4}$/, 'v.pairingCode') });

export interface KioskDeviceView {
  readonly name: string;
  readonly site: { readonly name: string; readonly timeZone: string };
  readonly branding: { readonly primaryColour: string | null; readonly contactLine: string | null };
  readonly settings: Pick<KioskSettingsView, 'defaultLocale' | 'idleSeconds' | 'warningSeconds' | 'lookupCheck' | 'checkIn' | 'selfCheckout' | 'walkInRequests' | 'acceptNotices' | 'badge'>;
  /** Only the hosts configured for walk-in requests; never the directory (OR-04). */
  readonly walkInHosts: ReadonlyArray<{ readonly id: string; readonly name: string }>;
}

export const KioskStartRequest = z.union([
  z.object({ purpose: z.enum(['check_in', 'check_out']), locale: z.enum(['en', 'ar']), pass: z.string().trim().min(20, 'v.invalid').max(500, 'v.invalid') }),
  z.object({ purpose: z.enum(['check_in', 'check_out']), locale: z.enum(['en', 'ar']), reference: z.string().trim().regex(/^[Vv]-?[0-9A-Fa-f]{6}$/, 'v.reference'), check: trimmed(100) }),
]);

export type KioskOutcomeDto = 'ready' | 'too_early' | 'ended' | 'already_onsite' | 'requirements' | 'see_reception' | 'not_onsite' | 'checked_in' | 'checked_out';

/** What a shared screen shows a visitor about their own visit: first name, host, window, next step. */
export interface KioskSessionView {
  readonly sessionToken?: string;
  readonly expiresAt: string;
  readonly purpose: 'check_in' | 'check_out';
  readonly visitor: { readonly givenName: string };
  readonly visit: { readonly reference: string; readonly title: string | null; readonly hostName: string; readonly startsAt: string; readonly endsAt: string; readonly destination: string | null };
  readonly outcome: KioskOutcomeDto;
  /** Present when check-in may be attempted; the server re-evaluates on apply. */
  readonly evaluationToken: string | null;
  readonly notices: ReadonlyArray<{ readonly noticeId: string; readonly key: string; readonly kind: 'acknowledgement' | 'signature'; readonly number: number; readonly title: string; readonly body: string; readonly purpose: string | null; readonly status: string }>;
  /** Requirements that cannot be completed at the kiosk (contact, documents, unpublished notices). */
  readonly otherOutstanding: number;
  readonly cardHeld: boolean;
  readonly assistanceRequested: boolean;
  /** PB-05-12 (PD-514): this journey's badge print after check-in; null when the kiosk does not print. */
  readonly badge?: KioskBadgeStatus | null;
}

export const KioskAcknowledgeRequest = z.object({ noticeId: Uuid, decision: z.enum(['accepted', 'declined']), signedName: z.string().trim().max(200, 'v.tooLong').optional() });
export const KioskCheckInRequest = z.object({ evaluationToken: z.string().min(1).max(100) });
export const KIOSK_ASSISTANCE_REASONS = ['general', 'cannot_find', 'scanner', 'requirements', 'not_ready', 'card'] as const;
export const KioskAssistanceRequest = z.object({ reason: z.enum(KIOSK_ASSISTANCE_REASONS) });
export const KioskEndRequest = z.object({ reason: z.enum(['finished', 'timeout', 'cancelled']) });
export const KioskWalkInRequest = z.object({
  locale: z.enum(['en', 'ar']),
  givenName: trimmed(100),
  familyName: optionalText(100),
  organisation: optionalText(200),
  hostId: Uuid,
  purpose: optionalText(200),
});
